import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import { articleDatabase } from '@/mocks/articles';
import { latestArticles } from '@/mocks/home';

const articleDetailImages: Record<string, string> = {
  "article-detail-01": "https://readdy.ai/api/search-image?query=Film%20awards%20ceremony%20scene%2C%20golden%20trophies%20on%20red%20carpet%2C%20warm%20spotlight%20lighting%2C%20elegant%20cinema%20atmosphere%2C%20soft%20bokeh%20background%2C%20sophisticated%20composition%2C%20high%20detail%2C%208K%20quality&width=1200&height=600&seq=article-detail-01&orientation=landscape",
  "article-detail-02": "https://readdy.ai/api/search-image?query=Shanghai%201990s%20neon%20street%20scene%20at%20night%2C%20warm%20amber%20and%20gold%20lighting%2C%20nostalgic%20urban%20atmosphere%2C%20cinematic%20Wong%20Kar-wai%20aesthetics%2C%20soft%20focus%2C%20elegant%20composition%2C%20high%20detail%2C%208K%20quality&width=1200&height=600&seq=article-detail-02&orientation=landscape",
  "article-detail-03": "https://readdy.ai/api/search-image?query=Chinese%20animation%20evolution%20concept%20art%2C%20traditional%20ink%20painting%20transitioning%20to%20modern%203D%20rendering%2C%20warm%20golden%20and%20vermillion%20tones%2C%20artistic%20composition%2C%20creative%20visual%20storytelling%2C%20high%20detail%2C%208K%20quality&width=1200&height=600&seq=article-detail-03&orientation=landscape",
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleDatabase[slug] : null;

  if (!article) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-800 mb-3">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm cursor-pointer">返回首页</Link>
        </div>
      </main>
    );
  }

  const relatedArticles = latestArticles.filter(
    (a) => article.relatedSlugs.includes(a.slug)
  );

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mangovideo.com/' },
          { '@type': 'ListItem', position: 2, name: article.category, item: 'https://mangovideo.com/category/zixun' },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        publisher: { '@type': 'Organization', name: '芒果视频' },
      },
      ...(article.faqs ? [{
        '@type': 'FAQPage',
        mainEntity: article.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      }] : []),
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${article.title} - 芒果视频`}
        description={article.excerpt}
        keywords={`芒果视频,${article.category},影视资讯,影评`}
        canonicalUrl={`https://mangovideo.com/article/${article.slug}`}
        ogType="article"
        schema={articleSchema}
      />

      <main className="pt-16">
        <nav className="px-4 md:px-6 py-4 bg-background-100 border-b border-background-200/70" aria-label="面包屑导航">
          <div className="max-w-4xl mx-auto">
            <ol className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
              <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to="/category/zixun" className="hover:text-primary-500 transition-colors cursor-pointer">资讯</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-700 font-medium truncate max-w-[200px]">{article.title}</li>
            </ol>
          </div>
        </nav>

        <article className="py-10 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 text-xs text-foreground-400">
                <span className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full">
                  {article.category}
                </span>
                <span>{article.date}</span>
                <span>{article.readTime}</span>
                <span>作者：{article.author}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            <div className="aspect-[2/1] rounded-xl overflow-hidden mb-10 bg-background-200">
              <img
                src={articleDetailImages[article.imageSeq] || articleDetailImages["article-detail-01"]}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom max-w-none">
              {article.content.map((paragraph, idx) => (
                <p key={idx} className="text-foreground-700 text-sm md:text-base leading-relaxed mb-5">
                  {paragraph}
                </p>
              ))}
            </div>

            {article.faqs && article.faqs.length > 0 && (
              <section className="mt-12 p-6 md:p-8 bg-background-100 rounded-xl">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  相关问答
                </h2>
                <div className="space-y-4">
                  {article.faqs.map((faq, idx) => (
                    <div key={idx}>
                      <h3 className="font-semibold text-foreground-800 text-sm md:text-base mb-2">
                        Q: {faq.question}
                      </h3>
                      <p className="text-foreground-500 text-sm">
                        A: {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="flex gap-4 p-4 bg-background-100 rounded-xl hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      <div className="w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-background-200">
                        <img
                          src={
                            ra.slug === '2024-nian-zui-jia-dian-ying-pan-dian'
                              ? "https://readdy.ai/api/search-image?query=Film%20awards%20ceremony%20scene%2C%20golden%20trophies%20on%20red%20carpet%2C%20warm%20spotlight%20lighting%2C%20elegant%20cinema%20atmosphere%2C%208K%20quality&width=160&height=160&seq=article-related-01&orientation=squarish"
                              : ra.slug === 'fan-hua-wei-shen-me-neng-huo-bian-quan-wang'
                                ? "https://readdy.ai/api/search-image?query=Shanghai%201990s%20neon%20street%20scene%20at%20night%2C%20warm%20amber%20lighting%2C%20nostalgic%20urban%20atmosphere%2C%208K%20quality&width=160&height=160&seq=article-related-02&orientation=squarish"
                                : "https://readdy.ai/api/search-image?query=Chinese%20animation%20concept%20art%2C%20traditional%20ink%20painting%20style%2C%20warm%20golden%20tones%2C%208K%20quality&width=160&height=160&seq=article-related-03&orientation=squarish"
                          }
                          alt={ra.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-foreground-400">{ra.date}</span>
                        <h3 className="font-medium text-foreground-800 text-sm line-clamp-2 mt-1">
                          {ra.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
    </>
  );
}